import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PlusIcon, TrashIcon, PencilIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { PageLayout } from '../../components/layout/PageLayout'
import { Pagination } from '../../components/ui/Pagination'
import { Input } from '../../components/ui/Input'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { SegmentedButton } from '../../components/ui/SegmentedButton'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { useToast } from '../../hooks/useToast'
import { useIngredients } from '../../hooks/useIngredients'
import { useDebounce } from '../../hooks/useDebounce'
import { ToastContainer } from '../../components/ui/Toast'

export const IngredientsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { 
    ingredients, 
    isLoading, 
    filters,
    setFilters, 
    resetFilters,
    deleteIngredient,
    refetch,
    invalidateCache,
  } = useIngredients()
  
  const [searchQuery, setSearchQuery] = useState(filters.search || '')
  const [sortColumn, setSortColumn] = useState<string>('inciName')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [ingredientToDelete, setIngredientToDelete] = useState<{ uuid: string, name: string } | null>(null)
  const { toasts, showToast, removeToast } = useToast()
  const processedStateRef = useRef(false)
  
  const debouncedSearch = useDebounce(searchQuery, 500)

  // Apply filter from navigation state and handle toast
  useEffect(() => {
    // Process location state only if it exists and hasn't been processed yet
    if (!location.state || processedStateRef.current) return
    
    // Mark as processed to prevent double execution in StrictMode
    processedStateRef.current = true
    
    let shouldRefresh = false
    
    if (location.state.toValidate === true) {
      setFilters({ toValidate: true, page: 1 })
      shouldRefresh = true
    }
    
    // Trigger refresh if requested (e.g., after validation from details page)
    if (location.state.triggerRefresh) {
      shouldRefresh = true
    }
    
    // Show toast if coming from delete action
    if (location.state.showToast) {
      showToast(location.state.toastMessage, location.state.toastType)
    }
    
    // Force refresh if needed
    if (shouldRefresh) {
      // Force invalidate the cache and refetch
      invalidateCache()
      refetch()
    }
    
    // Clear the state after processing
    window.history.replaceState({}, document.title)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  useEffect(() => {
    // Only search if we have 0 characters (reset) or at least 3 characters
    if (searchQuery.length === 0 || searchQuery.length >= 3) {
      if (debouncedSearch !== filters.search) {
        setFilters({ search: debouncedSearch, page: 1 })
        refetch()
      }
    }
  }, [debouncedSearch, filters.search, setFilters, refetch])

  // Listen for ingredient updates from detail pages
  useEffect(() => {
    const handleIngredientUpdate = () => {
      refetch()
    }

    window.addEventListener('ingredientUpdated', handleIngredientUpdate)
    return () => {
      window.removeEventListener('ingredientUpdated', handleIngredientUpdate)
    }
  }, [refetch])

  const handleSort = (column: string) => {
    let newDirection: 'asc' | 'desc' = 'asc'
    
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    }
    
    setSortColumn(column)
    setSortDirection(newDirection)
    
    const sortBy = newDirection === 'desc' ? `-${column}` : column
    setFilters({ sortBy })
    refetch()
  }


  const handleDelete = (uuid: string, name: string) => {
    setIngredientToDelete({ uuid, name })
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (ingredientToDelete) {
      try {
        await deleteIngredient(ingredientToDelete.uuid)
        setIngredientToDelete(null)
        
        // Show toast after successful deletion
        setTimeout(() => {
          showToast('Ingrédient supprimé avec succès', 'success')
        }, 100)
      } catch (error) {
        console.error('Error deleting ingredient:', error)
        showToast('Erreur lors de la suppression de l\'ingrédient', 'error')
      }
    }
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    resetFilters()
    refetch()
  }

  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || filters.toValidate !== null
  }

  const getSortIcon = (column: string) => {
    if (sortColumn !== column) return '↕'
    return sortDirection === 'asc' ? '↑' : '↓'
  }

  return (
    <PageLayout>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Ingrédients</h1>
        <Button
          onClick={refetch}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      {/* Filters Card */}
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">Filtres</h2>
          <Button
            onClick={() => navigate('/ingredients/new')}
            className="flex items-center gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Nouvel ingrédient
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <Input
            type="text"
            placeholder="Nom commun, nom INCI, numéro CAS, numéro EC... (min. 3 caractères)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        {/* Status filter and results count */}
        <div className="flex items-center justify-between">
          <SegmentedButton
            options={[
              { value: null, label: 'Tous' },
              { value: true, label: 'À valider' },
              { value: false, label: 'Validés' }
            ]}
            value={filters.toValidate}
            onChange={(value) => {
              setFilters({ toValidate: value, page: 1 })
              refetch()
            }}
          />

          <div className="flex items-center gap-4">
            {ingredients && (
              <span className="text-sm text-gray-600">
                {ingredients.totalRecords} ingrédient(s)
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters()}
            >
              Effacer les filtres
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th
                  onClick={() => handleSort('inciName')}
                  className="w-2/5 px-6 py-3 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center gap-1">
                    Nom INCI
                    <span className="text-gray-400">{getSortIcon('inciName')}</span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort('commonName')}
                  className="w-1/5 px-6 py-3 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center gap-1">
                    Nom commun
                    <span className="text-gray-400">{getSortIcon('commonName')}</span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort('casNumber')}
                  className="w-[10%] px-4 py-3 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center gap-1">
                    CAS
                    <span className="text-gray-400">{getSortIcon('casNumber')}</span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort('ecNumber')}
                  className="w-[10%] px-4 py-3 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center gap-1">
                    EC
                    <span className="text-gray-400">{getSortIcon('ecNumber')}</span>
                  </div>
                </th>
                <th className="w-[10%] px-4 py-3 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="w-[10%] px-4 py-3 text-left text-xs font-normal text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    Chargement...
                  </td>
                </tr>
              ) : !ingredients || ingredients.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    Aucun ingrédient trouvé
                  </td>
                </tr>
              ) : (
                ingredients.data.map((ingredient) => (
                  <tr
                    key={ingredient.uuid}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    onClick={() => navigate(`/ingredients/${ingredient.uuid}`)}
                  >
                    <td className="px-6 py-4 text-sm font-normal text-gray-900 dark:text-white truncate" title={ingredient.inciName}>
                      {ingredient.inciName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 truncate" title={ingredient.commonName || '-'}>
                      {ingredient.commonName || '-'}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 truncate" title={ingredient.casNumber || '-'}>
                      {ingredient.casNumber || '-'}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 truncate" title={ingredient.ecNumber || '-'}>
                      {ingredient.ecNumber || '-'}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          ingredient.toValidate
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}
                      >
                        {ingredient.toValidate ? 'À valider' : 'Validé'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                      <div 
                        className="flex items-center gap-2" 
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation()
                          }
                        }}
                        role="group"
                      >
                        <button
                          onClick={() => navigate(`/ingredients/${ingredient.uuid}`)}
                          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ingredient.uuid, ingredient.inciName)}
                          className="text-danger hover:text-danger/80"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {ingredients && ingredients.data.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={ingredients.currentPage}
              totalPages={ingredients.lastPage}
              pageSize={filters.limit}
              totalRecords={ingredients.totalRecords}
              onPageChange={(page) => {
                setFilters({ page })
                refetch()
              }}
              onPageSizeChange={(limit) => {
                setFilters({ limit, page: 1 })
                refetch()
              }}
            />
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setIngredientToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer l'ingrédient "${ingredientToDelete?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </PageLayout>
  )
}